import React, { PropsWithChildren, useCallback, useEffect } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useRecoilState } from "recoil"
import { SelectDataType } from "src/components/atom/SelectDataType"
import { LoginContext } from "src/contexts/LoginContext"
import { RESPONSE_CODE, SNACKBAR_TYPES, STORAGE_KEY } from "src/modules/const"
import { Adviser, AreaMaster, MasterDataService } from "src/openapi"
import { ShowToast } from "src/states/showToast"
import axios from "axios"

export const LoginProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation("translation", { keyPrefix: "base" })

  const navigate = useNavigate()

  const [, setShowToast] = useRecoilState(ShowToast)

  const [loggedin, setLoggedin] = useState<boolean>(false)
  const [accessToken, setAccessToken] = useState<string>("")
  const [awsID, setAwsID] = useState<string>("")
  const [userID, setUserID] = useState<number>()
  const [userType, setUserType] = useState<number>()
  const [jobs, setJobs] = useState<SelectDataType[]>([])
  const [areas, setAreas] = useState<SelectDataType[]>([])
  const [startupStages, setStartUpStages] = useState<SelectDataType[]>([])
  const [startupPlans, setStartupPlans] = useState<SelectDataType[]>([])
  const [routes, setRoutes] = useState<SelectDataType[]>([])
  const [companyTypes, setCompanyTypes] = useState<SelectDataType[]>([])
  const [eventCategories, setEventCategories] = useState<SelectDataType[]>([])
  const [areaDetails, setAreaDetails] = useState<AreaMaster[]>([])
  const [initialized, setInitialized] = useState<boolean>(false)

  const getAreaDetail = (id: number) => {
    for (const detail of areaDetails) {
      if (detail.id === id) {
        return detail
      }
    }
    return null
  }

  const getJobTitle = (id: number) => {
    for (const job of jobs) {
      if (job.value === id) {
        return job.label
      }
    }
    return ""
  }

  const getJobTitles = (adviser: Adviser) => {
    if (!adviser) {
      return ""
    }

    let ret = ""
    adviser.jobTitleId?.split(",").map((id) => {
      ret +=
        getJobTitle(parseInt(id)) === "その他"
          ? `${adviser.otherJobTitle}, `
          : `${getJobTitle(parseInt(id))}, `
    })

    if (ret.length >= 2) {
      ret = ret.slice(0, ret.length - 2)
    }

    return ret
  }

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEY.TOKEN)
    const awsID = localStorage.getItem(STORAGE_KEY.AWS_ID)
    const userType = localStorage.getItem(STORAGE_KEY.USER_TYPE)
      ? parseInt(localStorage.getItem(STORAGE_KEY.USER_TYPE) as string)
      : undefined

    if (token && awsID && userType) {
      setAccessToken(token)
      setAwsID(awsID)
      setUserType(userType)
      setLoggedin(true)
    } else {
      setLoggedin(false)
    }
    setInitialized(true)
  }, [])

  const handleMasterData = useCallback(
    (masterData: any) => {
      let jobs: SelectDataType[] = []
      for (const job of masterData?.adviserJobMasters as any) {
        jobs.push({
          value: job.id,
          label: job.jobTitle,
        })
      }

      let areas: SelectDataType[] = []
      for (const area of masterData?.areaMasters as any) {
        areas.push({
          value: area.id,
          label: area.area,
        })
      }

      let startupStages: SelectDataType[] = []
      for (const stage of masterData?.startupStageMasters as any) {
        startupStages.push({
          value: stage.id,
          label: stage.stage,
        })
      }

      let startupPlans: SelectDataType[] = []
      for (const plan of masterData?.startupPlanMasters as any) {
        startupPlans.push({
          value: plan.id,
          label: plan.plan,
        })
      }

      let routes: SelectDataType[] = []
      for (const route of masterData?.routeToKnowMasters as any) {
        routes.push({
          value: route.id,
          label: route.route,
        })
      }

      let companyTypes: SelectDataType[] = []
      for (const type of masterData?.startupBusinessTypeMasters as any) {
        companyTypes.push({
          value: type.id,
          label: type.type,
        })
      }

      let eventCategories: SelectDataType[] = []
      for (const category of masterData?.eventCategoryMasters as any) {
        eventCategories.push({
          value: category.id,
          label: category.category,
        })
      }

      setJobs(jobs)
      setAreas(areas)
      setAreaDetails(masterData.areaMasters)
      setStartUpStages(startupStages)
      setStartupPlans(startupPlans)
      setRoutes(routes)
      setCompanyTypes(companyTypes)
      setEventCategories(eventCategories)
    },
    [setJobs, setAreas, setStartUpStages, setStartupPlans, setRoutes, setCompanyTypes]
  )

  const refreshMasterData = useCallback(async () => {
    const res = await MasterDataService.masterDataList()
    if (res.status !== RESPONSE_CODE.SUCCESS) {
      return
    }

    handleMasterData(res.data)
    localStorage.setItem(STORAGE_KEY.MASTER_DATA, JSON.stringify(res.data))
  }, [handleMasterData])

  useEffect(() => {
    const masterData = localStorage.getItem(STORAGE_KEY.MASTER_DATA)
    if (!masterData) {
      refreshMasterData()
    } else {
      handleMasterData(JSON.parse(masterData))
    }
  }, [refreshMasterData, handleMasterData])

  const checkLoginStatus = (res: any) => {
    if (res.status === RESPONSE_CODE.UNAUTHORIZED) {
      logout()

      setShowToast({ show: true, text: t("please_signin"), type: SNACKBAR_TYPES.ERROR })
      navigate("/signin")
      return false
    }
    return true
  }

  const handleLogin = (token: string, userType: number, awsID: string) => {
    localStorage.setItem(STORAGE_KEY.TOKEN, token)
    localStorage.setItem(STORAGE_KEY.USER_TYPE, userType.toString())
    localStorage.setItem(STORAGE_KEY.AWS_ID, awsID)

    setAccessToken(token)
    setUserType(userType)
    setAwsID(awsID)
    setLoggedin(true)
  }

  const logout = () => {
    setLoggedin(false)
    setAccessToken("")
    setAwsID("")
    setUserID(undefined)

    localStorage.removeItem(STORAGE_KEY.TOKEN)
    localStorage.removeItem(STORAGE_KEY.AWS_ID)
    localStorage.removeItem(STORAGE_KEY.USER_TYPE)
    localStorage.removeItem(STORAGE_KEY.TEMP_USER_TYPE)
  }

  const getLineAccessToken = (code: string) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          "https://api.line.me/oauth2/v2.1/token",
          new URLSearchParams({
            grant_type: "authorization_code",
            code: code,
            redirect_uri: process.env.REACT_APP_LINE_CALLBACK_URL,
            client_id: process.env.REACT_APP_LINE_CHANNEL_ID,
            client_secret: process.env.REACT_APP_LINE_CHANNEL_SECRET,
          })
        )
        .then((res) => {
          resolve(res.data.access_token)
        })
        .catch((err) => {
          reject(err)
        })
    })
  }

  return (
    <LoginContext.Provider
      value={{
        loggedin,
        accessToken,
        awsID,
        userID,
        userType,
        jobs,
        areas,
        startupStages,
        startupPlans,
        routes,
        companyTypes,
        eventCategories,
        initialized,
        handleLogin,
        logout,
        refreshMasterData,
        checkLoginStatus,
        getAreaDetail,
        getJobTitle,
        getJobTitles,
        getLineAccessToken,
      }}
    >
      {children}
    </LoginContext.Provider>
  )
}
